
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CameraCard } from "./CameraCard";
import { useIsMobile } from "@/hooks/use-mobile";
import { EmptyState } from "@/components/ui/empty-state";
import { cameraSchema, CameraFormValues } from "@/types/inventory";
import { useCameraInventory } from "@/hooks/inventory/useCameraInventory";
import { PlusCircle, Camera } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { CameraSearchAndAdd } from "./CameraSearchAndAdd";
import { CameraEmptyState } from "./CameraEmptyState";

export function CameraInventoryContent() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { cameras, isLoading, isError, addCamera, refreshCameras } = useCameraInventory();
  const [activeSort, setActiveSort] = useState<string | null>("created_at");
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>("desc");

  const form = useForm<CameraFormValues>({
    resolver: zodResolver(cameraSchema),
    defaultValues: {
      brand: "",
      model: "",
      camera_type: "",
      format: null,
      mount: null,
      status: "working",
      serial_number: "",
      purchase_date: "",
      purchase_price: "",
      notes: "",
      adapter_enabled: false,
      adapter_mounts: "",
    },
  });

  const handleAddCamera = async (data: CameraFormValues) => {
    await addCamera(data);
    form.reset();
    setIsDialogOpen(false);
  };

  const sortOptions = [
    { label: t("brand"), value: "brand" },
    { label: t("model"), value: "model" },
    { label: t("status"), value: "status" },
    { label: t("date_added"), value: "created_at" }
  ];

  const handleSortChange = (value: string) => {
    setActiveSort(value);
    if (!sortDirection) {
      setSortDirection('asc');
    }
  };

  const handleDirectionChange = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  };

  const sortedAndFilteredCameras = useMemo(() => {
    if (!cameras) return [];
    
    // First filter
    const filtered = cameras.filter((camera) =>
      camera.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
      camera.model.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    // Then sort
    return [...filtered].sort((a, b) => {
      // Handle string comparisons for brand, model, status
      if (activeSort === "brand" || activeSort === "model" || activeSort === "status") {
        const aValue = a[activeSort].toLowerCase();
        const bValue = b[activeSort].toLowerCase();
        
        if (sortDirection === "asc") {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } 
      // Handle date comparison for created_at
      else if (activeSort === "created_at") {
        const aDate = new Date(a.created_at || 0);
        const bDate = new Date(b.created_at || 0);
        
        if (sortDirection === "asc") {
          return aDate.getTime() - bDate.getTime();
        } else {
          return bDate.getTime() - aDate.getTime();
        }
      }
      
      return 0;
    });
  }, [cameras, searchQuery, activeSort, sortDirection]);

  // Always show the search and add component, even when there are no cameras
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-48 bg-muted rounded-md"></div>
          ))}
        </div>
      );
    }

    if (isError) {
      return (
        <div className="text-center py-10">
          <p className="text-red-500">{t('error_loading_cameras')}</p>
          <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
            {t('retry')}
          </Button>
        </div>
      );
    }

    if (!cameras || cameras.length === 0) {
      return (
        <CameraEmptyState 
          searchQuery={searchQuery}
          onAddCameraClick={() => setIsDialogOpen(true)}
        />
      );
    }

    if (sortedAndFilteredCameras.length === 0) {
      return (
        <div className="text-center py-10">
          <p className="text-muted-foreground">
            {t('no_cameras_found')} <strong>"{searchQuery}"</strong>
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedAndFilteredCameras.map((camera) => (
          <CameraCard key={camera.id} camera={camera} onActionComplete={refreshCameras} />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <CameraSearchAndAdd
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isAddDialogOpen={isDialogOpen}
        setIsAddDialogOpen={setIsDialogOpen}
        form={form}
        onSubmit={handleAddCamera}
        activeSort={activeSort}
        sortDirection={sortDirection}
        handleSortChange={handleSortChange}
        handleDirectionChange={handleDirectionChange}
        sortOptions={sortOptions}
      />

      {renderContent()}
    </div>
  );
}
